/**
 * Auth guard (auth.guard.ts).
 *
 * @version
 * 4.70.0 OAUI-678 Build "Driver Assignment" UI
 * 4.68.0 DEVOPS-928 Add environment route resolver to all Angular repos
 * 4.64.0 OAUI-646 Upgrades for Angular v16
 * 4.63.0 DEVOPS-715 order-admin-ui: setup new repo without vertical slicing
 * 4.37.0 OAUI-527 Slowdowns in order modals
 * 4.33.0 OAUI-468 Remove duplicate calls for constants and hostConfiguration
 * 4.26.0 OAUI-426 Add order management permissions
 * 4.26.0 OAUI-416 Add basic QR code order management route
 *
 * @author Ryan Sweeney <rsweeney@buildcentrix.com>
 * @copyright (c) 2021-2024 Build Centrix
 */
import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { WdLocalEnvironmentService } from '../services/environment/wd-local-environment.service';

@Injectable()
export class AuthGuard {
  /**
   * Constructor Initialization
   * @since 4.63.0
   */
  constructor(
    private router: Router,
    private wdLocalEnvironmentService: WdLocalEnvironmentService,
  ) {}

  /**
   * Has Route Permission
   * @property {Function} permissions.canManageWorkOrders()
   * @property {Function} permissions.canViewWorkOrders()
   * @property {Function} permissions.canViewLookaheadOrders()
   * @since 4.63.0
   */
  static hasRoutePermission(url: string, permissions: any): boolean {
    let hasPermission = true;
    if (url && permissions) {
      if (url.includes('/orders/manage')) {
        hasPermission = permissions.canManageWorkOrders();
      }

      if (url.includes('orders/list')) {
        hasPermission = permissions.canViewWorkOrders();
      }

      if (url.includes('shipping/drivers')) {
        hasPermission = permissions.canViewVehicleAssignments();
      }
    }
    return hasPermission;
  }

  // noinspection JSUnusedGlobalSymbols
  /**
   * Can activate route check
   */
  canActivate(
    _route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): boolean {
    const url = state.url;
    const permissions = this.wdLocalEnvironmentService.values.permissions;

    if (!permissions) {
      void this.router.navigate(['errors', '403']);
      return false;
    }

    if (!AuthGuard.hasRoutePermission(url, permissions)) {
      void this.router.navigate(['errors', '403']);
    }

    return true;
  }
}
